// src/landing/CriadorLink.jsx
//
// Página do link do Criador Parceiro — lesvita.com/ref/{CODIGO}.
//
// Não existe tela no handoff para ela (decisão registrada): usa os tokens e a
// seção "O que sua audiência ganha" da landing de criadores. Quem chega aqui
// é a AUDIÊNCIA do criador, então a página vende o presente do código:
// VitaCoins, dias de premium e o desconto no site.
//
// O código fica guardado (localStorage) para o /assinar e para o cadastro web.
// No app recém-instalado ele chega por deferred deep link: iOS pela área de
// transferência (o link canônico), Android pelo Install Referrer
// (`criador=CODIGO`). Sem isso a pessoa digita o código na etapa do cadastro.

import React, { useEffect, useState } from "react";
import SeloCriador from "../features/criador/SeloCriador";
import { guardarCupom, registrarClique } from "../features/criador/cupomCriador";
import { abrirApp, isAndroid, isIOS, isMobile } from "../utils/abrirApp";
import S from "../styles/landing/CriadorLink.module.css";

const APP_STORE_URL = "https://apps.apple.com/br/app/lesvita/id6753772111";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.lesvita.app";

const Check = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export default function CriadorLink({ criador, origem = "link" }) {
  const codigo = criador.CODIGO;
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    guardarCupom(codigo, { nome: criador.NOME, foto: criador.FOTO_URL, origem });
    registrarClique(codigo, origem);
  }, [codigo, criador.NOME, criador.FOTO_URL, origem]);

  const linkCanonico = `https://www.lesvita.com/ref/${codigo}`;

  const baixar = async () => {
    if (isIOS()) {
      // Deferred deep link: o app lê a área de transferência na 1ª abertura.
      try { await navigator.clipboard.writeText(linkCanonico); } catch { /* sem gesto/permissão */ }
      window.location.href = APP_STORE_URL;
      return;
    }
    if (isAndroid()) {
      window.location.href = `${PLAY_STORE_URL}&referrer=${encodeURIComponent(`criador=${codigo}`)}`;
      return;
    }
    window.location.href = "/baixar";
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(codigo);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1600);
    } catch { /* seleção manual */ }
  };

  const beneficios = [
    [`${criador.VITACOINS || 150} VitaCoins`, "na hora, ao criar a conta"],
    [`${criador.PREMIUM_DIAS || 7} dias de premium`, "ScanFood 6x/dia, ScanBody, VITA e mais"],
    [`${criador.DESCONTO_PCT || 5}% de desconto na assinatura`, "pelo site · soma com os 5% do Pix"],
  ];

  return (
    <div data-theme="dark" className={S.pagina}>
      <div className={S.tela}>
        <div className={S.brilho} />
        <span className={S.marca}>LesVita</span>

        <div className={S.criador}>
          <div className={S.avatarSelo}>
            {criador.FOTO_URL ? (
              <img src={criador.FOTO_URL} alt="" className={S.avatar} />
            ) : (
              <span className={S.avatar}>{String(criador.NOME || "L").charAt(0).toUpperCase()}</span>
            )}
            <span className={S.seloCanto}><SeloCriador size={28} glow={false} /></span>
          </div>
          <span className={S.caps}>PRESENTE DE CRIADOR PARCEIRO</span>
          <h1 className={S.titulo}>{criador.NOME || "Um criador"} te deu um começo com vantagem</h1>
          {criador.INSTAGRAM ? <span className={S.arroba}>@{criador.INSTAGRAM}</span> : null}
        </div>

        <div className={S.cartao}>
          <span className={S.capsClaro}>O QUE VOCÊ GANHA COM O CÓDIGO</span>
          {beneficios.map(([t, s]) => (
            <div key={t} className={S.beneficio}>
              <span className={S.check}><Check /></span>
              <div className={S.beneficioTexto}>
                <strong>{t}</strong>
                <span>{s}</span>
              </div>
            </div>
          ))}
        </div>

        <div className={S.codigo}>
          <div className={S.codigoTexto}>
            <span className={S.rotulo}>SEU CÓDIGO</span>
            <strong>{codigo}</strong>
          </div>
          <button type="button" className={S.copiar} onClick={copiar}>{copiado ? "Copiado" : "Copiar"}</button>
        </div>
        <span className={S.dica}>No app, use o código na etapa “Tem um código de criador?” logo depois de confirmar o celular.</span>

        <div className={S.espaco} />
        <div className={S.acoes}>
          {isMobile() ? (
            <button type="button" className={`${S.btn} ${S.btnPrimario}`} onClick={baixar}>
              Baixar o LesVita
            </button>
          ) : (
            <div className={S.lojas}>
              <a href={APP_STORE_URL} target="_blank" rel="noreferrer">App Store</a>
              <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer">Google Play</a>
            </div>
          )}
          <button type="button" className={`${S.btn} ${S.btnEscuro}`} onClick={() => abrirApp({ destino: "login", origem: "link_criador" })}>
            Já tenho o app
          </button>
          <a className={S.linkSite} href={`/assinar?c=${encodeURIComponent(codigo)}&origem=${encodeURIComponent(origem)}`}>
            Assinar pelo site com {criador.DESCONTO_PCT || 5}% off
          </a>
        </div>
      </div>
    </div>
  );
}
